import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useState, useMemo, useRef } from "react";
import {
  Plus, Sparkles, Send, Image, Hash, Calendar, Trash2, PenTool,
  FileText, LayoutTemplate, FolderOpen, BarChart3, Eye, Copy,
  CheckCircle2, XCircle, Clock, ArrowUpCircle, RotateCcw, Search,
  Filter, ChevronLeft, ChevronRight, Settings2, Wand2, ListChecks,
  GripVertical, Tag, Layers, History, ShieldCheck, AlertTriangle, Save, Star, ArrowUpDown, Gauge, Download
} from "lucide-react";

type Tab = "overview" | "all" | "calendar" | "templates" | "media" | "ai";

const STATUS_CFG: Record<string, { label: string; color: string; bg: string }> = {
  draft:     { label: "Draft",     color: "text-gray-600",   bg: "bg-gray-100" },
  review:    { label: "In Review", color: "text-amber-700",  bg: "bg-amber-50" },
  approved:  { label: "Approved",  color: "text-blue-700",   bg: "bg-blue-50" },
  published: { label: "Published", color: "text-emerald-700",bg: "bg-emerald-50" },
  rejected:  { label: "Rejected",  color: "text-red-700",    bg: "bg-red-50" },
};

const TYPE_ICONS: Record<string, string> = {
  post: "📝", story: "📸", reel: "🎬", article: "📰", thread: "🧵", video: "🎥", email: "✉️", ad: "📢", blog: "📓",
};

const TEMPLATE_CATEGORIES = ["all", "general", "social", "email", "blog", "ad", "announcement", "promotion"];

export default function ContentStudio() {
  const qc = useQueryClient();
  const mediaFileInputRef = useRef<HTMLInputElement | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [showCreate, setShowCreate] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showDetail, setShowDetail] = useState<string | null>(null);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editTemplate, setEditTemplate] = useState<any>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [viewName, setViewName] = useState("");
  const [viewDefault, setViewDefault] = useState(false);
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [aiTopic, setAiTopic] = useState("");
  const [aiPlatform, setAiPlatform] = useState("facebook");
  const [aiTone, setAiTone] = useState("professional");
  const [aiResult, setAiResult] = useState("");
  const [tplCategory, setTplCategory] = useState("all");
  const [tplSearch, setTplSearch] = useState("");
  const [form, setForm] = useState({ title: "", body: "", type: "post", hashtags: "", platforms: [] as string[], scheduledAt: "", mediaIds: [] as string[] });
  const [tplForm, setTplForm] = useState({ name: "", description: "", category: "general", type: "post", body: "", hashtags: "" });

  // ─── Queries ───────────────────────────────────────
  const statsQ = useQuery({ queryKey: ["content-stats"], queryFn: () => api.get<any>("/content/stats") });
  const contentQ = useQuery({
    queryKey: ["content", page, filterStatus, filterType, searchQ, sortBy, sortDir],
    queryFn: () => api.get<any>(`/content?page=${page}&limit=20${filterStatus ? `&status=${filterStatus}` : ""}${filterType ? `&type=${filterType}` : ""}${searchQ ? `&search=${searchQ}` : ""}${sortBy ? `&sortBy=${sortBy}` : ""}${sortDir ? `&sortDir=${sortDir}` : ""}`),
  });
  const viewsQ = useQuery({
    queryKey: ["content-views"],
    queryFn: () => api.get<any>("/content/views"),
    enabled: tab === "all",
  });
  const calendarQ = useQuery({
    queryKey: ["content-calendar", calMonth.toISOString()],
    queryFn: () => {
      const s = calMonth.toISOString();
      const e = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0, 23, 59, 59).toISOString();
      return api.get<any>(`/content/calendar/events?start=${s}&end=${e}`);
    },
    enabled: tab === "calendar",
  });
  const templatesQ = useQuery({
    queryKey: ["content-templates", tplCategory, tplSearch],
    queryFn: () => api.get<any>(`/content/templates/list?${tplCategory !== "all" ? `category=${tplCategory}&` : ""}${tplSearch ? `search=${tplSearch}` : ""}`),
    enabled: tab === "templates",
  });
  const mediaQ = useQuery({
    queryKey: ["content-media"],
    queryFn: () => api.get<any>("/media?limit=50"),
    enabled: tab === "media" || showCreate,
  });
  const detailQ = useQuery({
    queryKey: ["content-detail", showDetail],
    queryFn: () => api.get<any>(`/content/${showDetail}`),
    enabled: !!showDetail,
  });

  // ─── Mutations ─────────────────────────────────────
  const inv = () => { qc.invalidateQueries({ queryKey: ["content"] }); qc.invalidateQueries({ queryKey: ["content-stats"] }); };
  const createM = useMutation({ mutationFn: (b: any) => api.post("/content", b), onSuccess: () => { inv(); setShowCreate(false); resetForm(); } });
  const deleteM = useMutation({ mutationFn: (id: string) => api.delete(`/content/${id}`), onSuccess: inv });
  const duplicateM = useMutation({ mutationFn: (id: string) => api.post(`/content/${id}/duplicate`, {}), onSuccess: inv });
  const submitM = useMutation({ mutationFn: (id: string) => api.post(`/content/${id}/submit`, {}), onSuccess: () => { inv(); if (showDetail) qc.invalidateQueries({ queryKey: ["content-detail"] }); } });
  const approveM = useMutation({ mutationFn: (id: string) => api.post(`/content/${id}/approve`, {}), onSuccess: () => { inv(); if (showDetail) qc.invalidateQueries({ queryKey: ["content-detail"] }); } });
  const rejectM = useMutation({ mutationFn: ({ id, comment }: { id: string; comment: string }) => api.post(`/content/${id}/reject`, { comment }), onSuccess: () => { inv(); if (showDetail) qc.invalidateQueries({ queryKey: ["content-detail"] }); } });
  const publishM = useMutation({ mutationFn: (id: string) => api.post(`/content/${id}/publish`, {}), onSuccess: () => { inv(); if (showDetail) qc.invalidateQueries({ queryKey: ["content-detail"] }); } });
  const bulkDeleteM = useMutation({ mutationFn: (ids: string[]) => api.post("/content/bulk/delete", { ids }), onSuccess: () => { inv(); setSelected(new Set()); } });
  const bulkStatusM = useMutation({ mutationFn: ({ ids, status }: { ids: string[]; status: string }) => api.post("/content/bulk/status", { ids, status }), onSuccess: () => { inv(); setSelected(new Set()); } });
  const aiM = useMutation({ mutationFn: (b: any) => api.post<any>("/ai/generate/post", b), onSuccess: (d) => setAiResult(d.content) });
  const multiAiM = useMutation({ mutationFn: (b: any) => api.post<any>("/ai/generate/multi-platform", b), onSuccess: (d) => setAiResult(JSON.stringify(d.posts, null, 2)) });
  const createTplM = useMutation({ mutationFn: (b: any) => api.post("/content/templates", b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["content-templates"] }); setShowTemplateForm(false); resetTplForm(); } });
  const updateTplM = useMutation({ mutationFn: ({ id, ...b }: any) => api.put(`/content/templates/${id}`, b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["content-templates"] }); setShowTemplateForm(false); setEditTemplate(null); resetTplForm(); } });
  const deleteTplM = useMutation({ mutationFn: (id: string) => api.delete(`/content/templates/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["content-templates"] }) });
  const fromTplM = useMutation({ mutationFn: ({ id, title }: { id: string; title: string }) => api.post(`/content/from-template/${id}`, { title }), onSuccess: inv });
  const restoreM = useMutation({ mutationFn: ({ id, vId }: { id: string; vId: string }) => api.post(`/content/${id}/restore/${vId}`, {}), onSuccess: () => { inv(); qc.invalidateQueries({ queryKey: ["content-detail"] }); } });
  const uploadMediaM = useMutation({
    mutationFn: ({ file }: { file: File }) => {
      const body = new FormData();
      body.append("file", file);
      return api.upload<any>("/media/upload", body);
    },
    onSuccess: (asset) => {
      qc.invalidateQueries({ queryKey: ["content-media"] });
      setForm((current) => ({
        ...current,
        mediaIds: current.mediaIds.includes(asset.id) ? current.mediaIds : [...current.mediaIds, asset.id],
      }));
    },
  });
  const deleteMediaM = useMutation({
    mutationFn: (id: string) => api.delete(`/media/${id}`),
    onSuccess: (_result, id) => {
      qc.invalidateQueries({ queryKey: ["content-media"] });
      setForm((current) => ({ ...current, mediaIds: current.mediaIds.filter((item) => item !== id) }));
    },
  });
  const saveViewM = useMutation({
    mutationFn: (body: any) => api.post("/content/views", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-views"] });
      setViewName("");
      setViewDefault(false);
    },
  });
  const updateViewM = useMutation({
    mutationFn: ({ id, ...body }: any) => api.put(`/content/views/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["content-views"] }),
  });
  const deleteViewM = useMutation({
    mutationFn: (id: string) => api.delete(`/content/views/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["content-views"] }),
  });

  const resetForm = () => setForm({ title: "", body: "", type: "post", hashtags: "", platforms: [], scheduledAt: "", mediaIds: [] });
  const resetTplForm = () => setTplForm({ name: "", description: "", category: "general", type: "post", body: "", hashtags: "" });

  const items = contentQ.data?.items || [];
  const stats = statsQ.data || {};
  const enterprise = stats.enterprise || {};
  const views = viewsQ.data?.views || [];
  const mediaItems = mediaQ.data?.items || [];
  const toggleSel = (id: string) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };
  const toggleAll = () => { if (selected.size === items.length) setSelected(new Set()); else setSelected(new Set(items.map((i: any) => i.id))); };
  const toggleMediaSelection = (id: string) => {
    setForm((current) => ({
      ...current,
      mediaIds: current.mediaIds.includes(id)
        ? current.mediaIds.filter((item) => item !== id)
        : [...current.mediaIds, id],
    }));
  };
  const currentFilters = { status: filterStatus, type: filterType, searchQ, sortBy, sortDir };
  const applyView = (view: any) => {
    const f = view?.filters || {};
    setFilterStatus(f.status || "");
    setFilterType(f.type || "");
    setSearchQ(f.searchQ || "");
    setSortBy(f.sortBy || "updatedAt");
    setSortDir(f.sortDir === "asc" ? "asc" : "desc");
    setPage(1);
  };
  const isViewActive = (view: any) => {
    const f = view?.filters || {};
    return (
      (f.status || "") === filterStatus &&
      (f.type || "") === filterType &&
      (f.searchQ || "") === searchQ &&
      (f.sortBy || "updatedAt") === sortBy &&
      (f.sortDir === "asc" ? "asc" : "desc") === sortDir
    );
  };
  const contentRisk = (item: any) => {
    const now = Date.now();
    const updatedAgoDays = Math.floor((now - new Date(item.updatedAt).getTime()) / 86400000);
    if (item.status === "review" && updatedAgoDays >= 2) return { label: "SLA risk", tone: "bg-amber-50 text-amber-700" };
    if (item.status === "draft" && updatedAgoDays >= 7) return { label: "Stale draft", tone: "bg-red-50 text-red-700" };
    if (item.status === "approved" && !item.scheduledAt) return { label: "Not scheduled", tone: "bg-blue-50 text-blue-700" };
    return { label: "Healthy", tone: "bg-green-50 text-green-700" };
  };
  const commandScore = Math.max(0, Math.min(100, Math.round(
    (Number(enterprise.publishVelocityPct || 0) * 0.4) +
    ((100 - Math.min(Number(enterprise.reviewOverSla || 0) * 12, 100)) * 0.35) +
    (Number(enterprise.aiAdoptionPct || 0) * 0.25)
  )));

  // ─── Calendar helpers ──────────────────────────────
  const calDays = useMemo(() => {
    const y = calMonth.getFullYear(), m = calMonth.getMonth();
    const firstDay = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const days: { day: number; items: any[] }[] = [];
    for (let i = 0; i < firstDay; i++) days.push({ day: 0, items: [] });
    for (let d = 1; d <= daysInMonth; d++) {
      const dayItems = (calendarQ.data?.items || []).filter((it: any) => {
        const dt = it.scheduledAt || it.publishedAt || it.createdAt;
        if (!dt) return false;
        const dd = new Date(dt);
        return dd.getDate() === d && dd.getMonth() === m && dd.getFullYear() === y;
      });
      days.push({ day: d, items: dayItems });
    }
    return days;
  }, [calMonth, calendarQ.data]);

  const TABS: { key: Tab; icon: any; label: string }[] = [
    { key: "overview", icon: BarChart3, label: "Overview" },
    { key: "all", icon: FileText, label: "All Content" },
    { key: "calendar", icon: Calendar, label: "Calendar" },
    { key: "templates", icon: LayoutTemplate, label: "Templates" },
    { key: "media", icon: FolderOpen, label: "Media" },
    { key: "ai", icon: Sparkles, label: "AI Studio" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <PenTool className="w-6 h-6 text-purple-600" /> Content Studio
          </h1>
          <p className="text-sm text-gray-500 mt-1">Create, schedule, approve and manage your content pipeline</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => { setTab("ai"); setShowAI(true); }} className="btn-secondary"><Sparkles className="w-4 h-4" /> AI Generate</button>
          <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus className="w-4 h-4" /> New Content</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.key ? "border-purple-600 text-purple-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* ═══ OVERVIEW TAB ═══ */}
      {tab === "overview" && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: "Total", value: stats.total || 0, icon: FileText, color: "text-purple-600 bg-purple-50" },
              { label: "Drafts", value: stats.draft || 0, icon: PenTool, color: "text-gray-600 bg-gray-100" },
              { label: "In Review", value: stats.review || 0, icon: Eye, color: "text-amber-600 bg-amber-50" },
              { label: "Approved", value: stats.approved || 0, icon: CheckCircle2, color: "text-blue-600 bg-blue-50" },
              { label: "Published", value: stats.published || 0, icon: ArrowUpCircle, color: "text-emerald-600 bg-emerald-50" },
              { label: "Scheduled", value: stats.scheduled || 0, icon: Clock, color: "text-indigo-600 bg-indigo-50" },
            ].map((c) => (
              <div key={c.label} className="card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`p-2 rounded-lg ${c.color}`}><c.icon className="w-4 h-4" /></span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{c.value}</p>
                <p className="text-xs text-gray-500">{c.label}</p>
              </div>
            ))}
          </div>

          {/* Enterprise Command Center */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card p-5 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Gauge className="w-4 h-4 text-indigo-600" /> Command Score</h3>
                <span className="text-xs font-semibold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">Enterprise</span>
              </div>
              <p className="text-3xl font-bold text-gray-900">{commandScore}</p>
              <div className="w-full h-2 bg-gray-100 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" style={{ width: `${commandScore}%` }} />
              </div>
              <p className="text-xs text-gray-500 mt-2">Blends publish velocity, SLA compliance, and AI adoption.</p>
            </div>
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">SLA Risk Radar</h3>
              <div className="space-y-2 text-xs">
                <p className="flex items-center justify-between"><span className="text-gray-500">Review over SLA</span><span className={`font-semibold ${enterprise.reviewOverSla > 0 ? "text-amber-600" : "text-green-600"}`}>{enterprise.reviewOverSla || 0}</span></p>
                <p className="flex items-center justify-between"><span className="text-gray-500">Stale drafts</span><span className={`font-semibold ${enterprise.staleDrafts > 0 ? "text-red-600" : "text-green-600"}`}>{enterprise.staleDrafts || 0}</span></p>
                <p className="flex items-center justify-between"><span className="text-gray-500">Publish velocity (7d)</span><span className="font-semibold text-blue-700">{enterprise.publishVelocityPct || 0}%</span></p>
              </div>
            </div>
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Automation Footprint</h3>
              <div className="space-y-2 text-xs">
                <p className="flex items-center justify-between"><span className="text-gray-500">AI generated</span><span className="font-semibold text-purple-700">{enterprise.aiGenerated || 0}</span></p>
                <p className="flex items-center justify-between"><span className="text-gray-500">AI adoption</span><span className="font-semibold text-purple-700">{enterprise.aiAdoptionPct || 0}%</span></p>
                <p className="flex items-center justify-between"><span className="text-gray-500">Campaign-linked</span><span className="font-semibold text-indigo-700">{enterprise.campaignLinkPct || 0}%</span></p>
              </div>
            </div>
          </div>

          {/* Two-column: Type Breakdown + Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2"><Layers className="w-4 h-4" /> Content by Type</h3>
              {stats.typeBreakdown && Object.keys(stats.typeBreakdown).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(stats.typeBreakdown).map(([type, count]: [string, any]) => (
                    <div key={type} className="flex items-center gap-3">
                      <span className="text-lg">{TYPE_ICONS[type] || "📄"}</span>
                      <span className="text-sm font-medium text-gray-700 capitalize w-20">{type}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-2.5">
                        <div className="bg-gradient-to-r from-violet-500 to-purple-500 h-2.5 rounded-full" style={{ width: `${Math.min(100, (count / Math.max(stats.total, 1)) * 100)}%` }} />
                      </div>
                      <span className="text-sm font-semibold text-gray-600 w-8 text-right">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No content yet</p>
              )}
            </div>
            <div className="card p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2"><History className="w-4 h-4" /> Recent Activity</h3>
              {stats.recent?.length ? (
                <div className="space-y-3">
                  {stats.recent.map((r: any) => (
                    <div key={r.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg -mx-2" onClick={() => setShowDetail(r.id)}>
                      <span className="text-lg">{TYPE_ICONS[r.type] || "📄"}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{r.title}</p>
                        <p className="text-xs text-gray-400">{new Date(r.updatedAt).toLocaleDateString()}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CFG[r.status]?.bg || "bg-gray-100"} ${STATUS_CFG[r.status]?.color || "text-gray-600"}`}>
                        {STATUS_CFG[r.status]?.label || r.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No recent content</p>
              )}
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card p-5 flex items-center gap-4 cursor-pointer hover:border-purple-200" onClick={() => setTab("templates")}>
              <div className="p-3 bg-pink-50 rounded-xl"><LayoutTemplate className="w-6 h-6 text-pink-600" /></div>
              <div>
                <p className="text-lg font-bold text-gray-900">{stats.templates || 0}</p>
                <p className="text-sm text-gray-500">Content Templates</p>
              </div>
            </div>
            <div className="card p-5 flex items-center gap-4 cursor-pointer hover:border-purple-200" onClick={() => { setFilterStatus("rejected"); setTab("all"); }}>
              <div className="p-3 bg-red-50 rounded-xl"><AlertTriangle className="w-6 h-6 text-red-600" /></div>
              <div>
                <p className="text-lg font-bold text-gray-900">{stats.rejected || 0}</p>
                <p className="text-sm text-gray-500">Needs Revision</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ALL CONTENT TAB ═══ */}
      {tab === "all" && (
        <div className="space-y-4">
          {/* Ops Strip */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="card p-4 border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
              <p className="text-xs text-gray-500 flex items-center gap-1"><Gauge className="w-3.5 h-3.5 text-indigo-600" /> Command Score</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{commandScore}</p>
              <p className="text-[11px] text-gray-500">Governance + velocity + automation blend</p>
            </div>
            <div className="card p-4">
              <p className="text-xs text-gray-500">Review over SLA</p>
              <p className={`text-2xl font-bold mt-1 ${enterprise.reviewOverSla > 0 ? "text-amber-600" : "text-green-600"}`}>{enterprise.reviewOverSla || 0}</p>
              <p className="text-[11px] text-gray-500">Older than 48h in review</p>
            </div>
            <div className="card p-4">
              <p className="text-xs text-gray-500">Stale Drafts</p>
              <p className={`text-2xl font-bold mt-1 ${enterprise.staleDrafts > 0 ? "text-red-600" : "text-green-600"}`}>{enterprise.staleDrafts || 0}</p>
              <p className="text-[11px] text-gray-500">No updates in 7+ days</p>
            </div>
            <div className="card p-4">
              <p className="text-xs text-gray-500">Publish Velocity (7d)</p>
              <p className="text-2xl font-bold mt-1 text-blue-700">{enterprise.publishVelocityPct || 0}%</p>
              <p className="text-[11px] text-gray-500">{enterprise.published7d || 0} published / {enterprise.created7d || 0} created</p>
            </div>
          </div>

          {/* Filters + Search */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className="input pl-10" placeholder="Search content..." value={searchQ} onChange={(e) => { setSearchQ(e.target.value); setPage(1); }} />
            </div>
            <select className="input w-auto" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select className="input w-auto" value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {Object.keys(TYPE_ICONS).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="input w-auto" value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
              <option value="updatedAt">Sort: Updated</option>
              <option value="createdAt">Sort: Created</option>
              <option value="title">Sort: Title</option>
              <option value="status">Sort: Status</option>
              <option value="type">Sort: Type</option>
              <option value="scheduledAt">Sort: Scheduled</option>
              <option value="publishedAt">Sort: Published</option>
            </select>
            <button
              onClick={() => { setSortDir(sortDir === "desc" ? "asc" : "desc"); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              {sortDir === "desc" ? "Descending" : "Ascending"}
            </button>
            {selected.size > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-gray-500 font-medium">{selected.size} selected</span>
                <button onClick={() => bulkStatusM.mutate({ ids: [...selected], status: "review" })} className="btn-secondary text-xs py-1">Submit All</button>
                <button onClick={() => bulkStatusM.mutate({ ids: [...selected], status: "approved" })} className="btn-secondary text-xs py-1">Approve All</button>
                <button onClick={() => { if (confirm(`Delete ${selected.size} items?`)) bulkDeleteM.mutate([...selected]); }} className="text-xs py-1 px-3 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 font-medium">Delete</button>
              </div>
            )}
          </div>

          {/* Saved Views */}
          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Save className="w-4 h-4 text-purple-600" /> Saved Views</h3>
              <span className="text-xs text-gray-500">{views.length} saved</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {views.length > 0 ? views.map((view: any) => (
                <div key={view.id} className={`inline-flex items-center rounded-lg border px-2 py-1 ${isViewActive(view) ? "border-purple-300 bg-purple-50" : "border-gray-200 bg-white"}`}>
                  <button onClick={() => applyView(view)} className={`text-xs font-medium ${isViewActive(view) ? "text-purple-700" : "text-gray-700 hover:text-purple-700"}`}>{view.name}</button>
                  {view.isDefault && <Star className="w-3 h-3 ml-1 text-amber-500 fill-amber-400" />}
                  {!view.isDefault && <button onClick={() => updateViewM.mutate({ id: view.id, isDefault: true })} className="ml-1 text-[10px] text-gray-400 hover:text-amber-500">default</button>}
                  <button onClick={() => { if (confirm(`Delete "${view.name}"?`)) deleteViewM.mutate(view.id); }} className="ml-2 text-gray-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                </div>
              )) : <p className="text-xs text-gray-400">No saved views yet.</p>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input className="input h-9 text-xs w-52" placeholder="View name" value={viewName} onChange={(e) => setViewName(e.target.value)} />
              <label className="flex items-center gap-1 text-xs text-gray-500">
                <input type="checkbox" checked={viewDefault} onChange={(e) => setViewDefault(e.target.checked)} className="rounded border-gray-300" />
                set as default
              </label>
              <button
                onClick={() => saveViewM.mutate({ name: viewName.trim(), filters: currentFilters, isDefault: viewDefault })}
                disabled={!viewName.trim() || saveViewM.isPending}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {saveViewM.isPending ? "Saving..." : "Save Current View"}
              </button>
            </div>
          </div>

          {/* Content Table */}
          {contentQ.isLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" /></div>
          ) : items.length > 0 ? (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <th className="p-3 w-10"><input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={toggleAll} className="rounded" /></th>
                    <th className="p-3">Content</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Author</th>
                    <th className="p-3">Scheduled</th>
                    <th className="p-3">Signal</th>
                    <th className="p-3">Updated</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item: any) => {
                    const risk = contentRisk(item);
                    return (
                    <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="p-3"><input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSel(item.id)} className="rounded" /></td>
                      <td className="p-3">
                        <button onClick={() => setShowDetail(item.id)} className="text-left">
                          <p className="text-sm font-semibold text-gray-900 hover:text-purple-700 transition-colors">{item.title}</p>
                          <p className="text-xs text-gray-400 line-clamp-1 mt-0.5 max-w-[300px]">{item.body}</p>
                        </button>
                      </td>
                      <td className="p-3"><span className="text-sm">{TYPE_ICONS[item.type] || "📄"} <span className="capitalize text-gray-600">{item.type}</span></span></td>
                      <td className="p-3">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_CFG[item.status]?.bg || "bg-gray-100"} ${STATUS_CFG[item.status]?.color || "text-gray-600"}`}>
                          {STATUS_CFG[item.status]?.label || item.status}
                        </span>
                      </td>
                      <td className="p-3"><span className="text-sm text-gray-600">{item.createdBy?.name || "—"}</span></td>
                      <td className="p-3"><span className="text-sm text-gray-500">{item.scheduledAt ? new Date(item.scheduledAt).toLocaleDateString() : "—"}</span></td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${risk.tone}`}>{risk.label}</span>
                      </td>
                      <td className="p-3"><span className="text-xs text-gray-400">{new Date(item.updatedAt).toLocaleDateString()}</span></td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          {item.status === "draft" && <button onClick={() => submitM.mutate(item.id)} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600" title="Submit for Review"><ArrowUpCircle className="w-4 h-4" /></button>}
                          {item.status === "review" && <button onClick={() => approveM.mutate(item.id)} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600" title="Approve"><CheckCircle2 className="w-4 h-4" /></button>}
                          {item.status === "approved" && <button onClick={() => publishM.mutate(item.id)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600" title="Publish"><Send className="w-4 h-4" /></button>}
                          <button onClick={() => duplicateM.mutate(item.id)} className="p-1.5 rounded-lg hover:bg-purple-50 text-purple-600" title="Duplicate"><Copy className="w-4 h-4" /></button>
                          <button onClick={() => { if (confirm("Delete?")) deleteM.mutate(item.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card p-12 text-center">
              <FileText className="w-12 h-12 text-purple-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-1">No content found</h3>
              <p className="text-sm text-gray-500 mb-4">Create your first content piece or adjust your filters.</p>
              <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus className="w-4 h-4" /> New Content</button>
            </div>
          )}

          {/* Pagination */}
          {contentQ.data && contentQ.data.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Page {contentQ.data.page} of {contentQ.data.pages} ({contentQ.data.total} items)</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="btn-secondary text-sm py-1"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setPage(Math.min(contentQ.data.pages, page + 1))} disabled={page >= contentQ.data.pages} className="btn-secondary text-sm py-1"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ CALENDAR TAB ═══ */}
      {tab === "calendar" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))} className="btn-secondary text-sm"><ChevronLeft className="w-4 h-4" /></button>
            <h3 className="text-lg font-semibold text-gray-800">{calMonth.toLocaleString("default", { month: "long", year: "numeric" })}</h3>
            <button onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))} className="btn-secondary text-sm"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="card overflow-hidden">
            <div className="grid grid-cols-7 bg-gray-50 text-center text-xs font-semibold text-gray-500 uppercase">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="p-2 border-b border-gray-200">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {calDays.map((d, i) => (
                <div key={i} className={`min-h-[100px] border-b border-r border-gray-100 p-1.5 ${d.day === 0 ? "bg-gray-50/50" : "hover:bg-purple-50/30"} ${d.day === new Date().getDate() && calMonth.getMonth() === new Date().getMonth() && calMonth.getFullYear() === new Date().getFullYear() ? "bg-purple-50/50" : ""}`}>
                  {d.day > 0 && (
                    <>
                      <span className={`text-xs font-medium ${d.day === new Date().getDate() && calMonth.getMonth() === new Date().getMonth() ? "text-purple-700 bg-purple-100 rounded-full w-6 h-6 flex items-center justify-center" : "text-gray-500"}`}>{d.day}</span>
                      <div className="mt-1 space-y-0.5">
                        {d.items.slice(0, 3).map((it: any) => (
                          <button key={it.id} onClick={() => setShowDetail(it.id)} className={`w-full text-left text-[10px] px-1.5 py-0.5 rounded truncate font-medium ${STATUS_CFG[it.status]?.bg || "bg-gray-100"} ${STATUS_CFG[it.status]?.color || "text-gray-600"}`}>
                            {TYPE_ICONS[it.type] || "📄"} {it.title}
                          </button>
                        ))}
                        {d.items.length > 3 && <span className="text-[10px] text-gray-400 pl-1">+{d.items.length - 3} more</span>}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ TEMPLATES TAB ═══ */}
      {tab === "templates" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-2 overflow-x-auto">
              {TEMPLATE_CATEGORIES.map((c) => (
                <button key={c} onClick={() => setTplCategory(c)} className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize whitespace-nowrap transition-colors ${tplCategory === c ? "bg-purple-100 text-purple-700" : "text-gray-500 hover:bg-gray-100"}`}>{c}</button>
              ))}
            </div>
            <button onClick={() => { setEditTemplate(null); resetTplForm(); setShowTemplateForm(true); }} className="btn-primary text-sm"><Plus className="w-4 h-4" /> New Template</button>
          </div>

          <div className="relative max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input pl-10" placeholder="Search templates..." value={tplSearch} onChange={(e) => setTplSearch(e.target.value)} />
          </div>

          {templatesQ.isLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" /></div>
          ) : (templatesQ.data || []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(templatesQ.data || []).map((tpl: any) => (
                <div key={tpl.id} className="card p-5 hover:border-purple-200 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 font-medium capitalize">{tpl.category}</span>
                    <span className="text-xs text-gray-400">Used {tpl.usageCount}x</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-1">{tpl.name}</h4>
                  {tpl.description && <p className="text-xs text-gray-500 mb-2">{tpl.description}</p>}
                  <p className="text-sm text-gray-600 line-clamp-3 mb-4 bg-gray-50 rounded-lg p-2 font-mono text-xs">{tpl.body}</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => fromTplM.mutate({ id: tpl.id, title: tpl.name })} className="btn-primary text-xs py-1.5 flex-1">Use Template</button>
                    <button onClick={() => { setEditTemplate(tpl); setTplForm({ name: tpl.name, description: tpl.description || "", category: tpl.category, type: tpl.type, body: tpl.body, hashtags: tpl.hashtags || "" }); setShowTemplateForm(true); }} className="btn-secondary text-xs py-1.5"><Settings2 className="w-3 h-3" /></button>
                    <button onClick={() => { if (confirm("Delete template?")) deleteTplM.mutate(tpl.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <LayoutTemplate className="w-12 h-12 text-purple-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-1">No templates yet</h3>
              <p className="text-sm text-gray-500 mb-4">Create reusable templates with placeholders like {"{{company}}"} or {"{{offer}}"}.</p>
              <button onClick={() => { resetTplForm(); setShowTemplateForm(true); }} className="btn-primary"><Plus className="w-4 h-4" /> Create Template</button>
            </div>
          )}
        </div>
      )}

      {/* ═══ MEDIA TAB ═══ */}
      {tab === "media" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{(mediaQ.data?.items || []).length} assets</p>
            <div className="flex items-center gap-3">
              {uploadMediaM.error && <span className="text-xs text-red-600">{(uploadMediaM.error as Error).message}</span>}
              <input
                ref={mediaFileInputRef}
                type="file"
                className="hidden"
                accept="image/*,video/*,.pdf"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) uploadMediaM.mutate({ file });
                  event.target.value = "";
                }}
              />
              <button onClick={() => mediaFileInputRef.current?.click()} className="btn-primary text-sm" disabled={uploadMediaM.isPending}>
                <Image className="w-4 h-4" /> {uploadMediaM.isPending ? "Uploading..." : "Upload Asset"}
              </button>
            </div>
          </div>
          {mediaQ.isLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" /></div>
          ) : (mediaQ.data?.items || []).length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {(mediaQ.data?.items || []).map((asset: any) => (
                <div key={asset.id} className="card p-2 hover:border-purple-200 transition-colors group">
                  {asset.mimeType?.startsWith("image/") ? (
                    <img src={asset.thumbnailDeliveryUrl || asset.deliveryUrl || asset.url} alt={asset.altText || asset.originalName} className="w-full h-24 object-cover rounded-lg" />
                  ) : (
                    <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <p className="text-xs text-gray-600 mt-2 truncate">{asset.originalName}</p>
                  <p className="text-[10px] text-gray-400">{(asset.size / 1024).toFixed(1)} KB</p>
                  <div className="mt-2 flex items-center gap-2">
                    <a href={asset.deliveryUrl || asset.url} target="_blank" rel="noreferrer" className="text-[10px] font-medium text-purple-600 hover:text-purple-800">Open</a>
                    <a href={asset.deliveryUrl || asset.url} target="_blank" rel="noreferrer" className="text-[10px] font-medium text-gray-500 hover:text-gray-700"><Download className="inline w-3 h-3" /></a>
                    <button
                      onClick={() => { if (confirm(`Delete ${asset.originalName}?`)) deleteMediaM.mutate(asset.id); }}
                      className="ml-auto text-[10px] font-medium text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <FolderOpen className="w-12 h-12 text-purple-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-1">Media Library Empty</h3>
              <p className="text-sm text-gray-500">Your uploaded images, videos, and files will appear here.</p>
            </div>
          )}
        </div>
      )}

      {/* ═══ AI STUDIO TAB ═══ */}
      {tab === "ai" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6 space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2"><Wand2 className="w-5 h-5 text-purple-600" /> AI Content Generator</h3>
            <div>
              <label className="label">Topic / Prompt</label>
              <textarea className="input min-h-[100px]" placeholder="e.g. Launch announcement for our new cloud hosting with 50% off first month..." value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Platform</label>
                <select className="input" value={aiPlatform} onChange={(e) => setAiPlatform(e.target.value)}>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="x">X (Twitter)</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="tiktok">TikTok</option>
                </select>
              </div>
              <div>
                <label className="label">Tone</label>
                <select className="input" value={aiTone} onChange={(e) => setAiTone(e.target.value)}>
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="humorous">Humorous</option>
                  <option value="urgent">Urgent</option>
                  <option value="inspirational">Inspirational</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => aiM.mutate({ topic: aiTopic, platform: aiPlatform, tone: aiTone })} disabled={!aiTopic || aiM.isPending} className="btn-primary flex-1">
                {aiM.isPending ? "Generating..." : "Generate Single"}
              </button>
              <button onClick={() => multiAiM.mutate({ topic: aiTopic, platforms: ["facebook", "instagram", "x", "linkedin", "tiktok"], tone: aiTone })} disabled={!aiTopic || multiAiM.isPending} className="btn-secondary flex-1">
                {multiAiM.isPending ? "Generating..." : "All Platforms"}
              </button>
            </div>
          </div>

          <div className="card p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4" /> Generated Content</h3>
            {aiResult ? (
              <div className="space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-[400px] overflow-y-auto">
                  <pre className="text-sm whitespace-pre-wrap text-gray-700 font-sans">{aiResult}</pre>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => { setForm({ ...form, title: aiTopic.slice(0, 50), body: aiResult }); setShowCreate(true); }} className="btn-primary flex-1"><Send className="w-4 h-4" /> Use This Content</button>
                  <button onClick={() => setAiResult("")} className="btn-secondary">Clear</button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Sparkles className="w-10 h-10 text-purple-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Generated content will appear here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ CREATE CONTENT MODAL ═══ */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><PenTool className="w-5 h-5 text-purple-600" /> New Content</h2>
            <form onSubmit={(e) => { e.preventDefault(); createM.mutate(form); }} className="space-y-4">
              <div>
                <label className="label">Title *</label>
                <input className="input" placeholder="My awesome post" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div>
                <label className="label">Content *</label>
                <textarea className="input min-h-[140px]" placeholder="Write your content..." value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Type</label>
                  <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    <option value="post">Post</option>
                    <option value="story">Story</option>
                    <option value="reel">Reel</option>
                    <option value="article">Article</option>
                    <option value="thread">Thread</option>
                    <option value="video">Video</option>
                    <option value="email">Email</option>
                    <option value="ad">Ad</option>
                    <option value="blog">Blog</option>
                  </select>
                </div>
                <div>
                  <label className="label">Hashtags</label>
                  <input className="input" placeholder="#marketing, #growth" value={form.hashtags} onChange={(e) => setForm({ ...form, hashtags: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="label">Schedule At (optional)</label>
                <input type="datetime-local" className="input" value={form.scheduledAt} onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label mb-0">Attach Media</label>
                  <span className="text-xs text-gray-400">{form.mediaIds.length} selected</span>
                </div>
                {mediaItems.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-64 overflow-y-auto rounded-xl border border-gray-200 p-3">
                    {mediaItems.map((asset: any) => {
                      const selectedMedia = form.mediaIds.includes(asset.id);
                      return (
                        <button
                          type="button"
                          key={asset.id}
                          onClick={() => toggleMediaSelection(asset.id)}
                          className={`rounded-xl border p-2 text-left transition-colors ${selectedMedia ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-purple-200"}`}
                        >
                          {asset.mimeType?.startsWith("image/") ? (
                            <img src={asset.thumbnailDeliveryUrl || asset.deliveryUrl || asset.url} alt={asset.altText || asset.originalName} className="w-full h-20 rounded-lg object-cover" />
                          ) : (
                            <div className="w-full h-20 rounded-lg bg-gray-100 flex items-center justify-center">
                              <FileText className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <p className="mt-2 truncate text-xs font-medium text-gray-700">{asset.originalName}</p>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                    Upload assets from the Media tab first, then attach them here.
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={createM.isPending} className="btn-primary">{createM.isPending ? "Creating..." : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ TEMPLATE FORM MODAL ═══ */}
      {showTemplateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowTemplateForm(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold mb-4">{editTemplate ? "Edit Template" : "New Template"}</h2>
            <form onSubmit={(e) => { e.preventDefault(); editTemplate ? updateTplM.mutate({ id: editTemplate.id, ...tplForm }) : createTplM.mutate(tplForm); }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Name *</label>
                  <input className="input" placeholder="Product Launch" value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} required />
                </div>
                <div>
                  <label className="label">Category</label>
                  <select className="input" value={tplForm.category} onChange={(e) => setTplForm({ ...tplForm, category: e.target.value })}>
                    {TEMPLATE_CATEGORIES.filter((c) => c !== "all").map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label">Description</label>
                <input className="input" placeholder="Template description" value={tplForm.description} onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })} />
              </div>
              <div>
                <label className="label">Template Body *</label>
                <textarea className="input min-h-[120px] font-mono text-sm" placeholder={"🚀 Exciting news! {{company}} is launching {{product}}!\n\n{{description}}\n\nGet {{offer}} for a limited time.\n\n{{hashtags}}"} value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })} required />
                <p className="text-xs text-gray-400 mt-1">Use {"{{variable}}"} for dynamic placeholders</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Type</label>
                  <select className="input" value={tplForm.type} onChange={(e) => setTplForm({ ...tplForm, type: e.target.value })}>
                    {Object.keys(TYPE_ICONS).map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Hashtags</label>
                  <input className="input" placeholder="#launch, #new" value={tplForm.hashtags} onChange={(e) => setTplForm({ ...tplForm, hashtags: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowTemplateForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={createTplM.isPending || updateTplM.isPending} className="btn-primary">{editTemplate ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ CONTENT DETAIL MODAL ═══ */}
      {showDetail && detailQ.data && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDetail(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const c = detailQ.data;
              const nowTs = Date.now();
              const ageDays = Math.max(0, Math.floor((nowTs - new Date(c.createdAt).getTime()) / 86400000));
              const reviewDays = c.status === "review" ? Math.max(0, Math.floor((nowTs - new Date(c.updatedAt).getTime()) / 86400000)) : 0;
              const hasSchedule = !!c.scheduledAt;
              const readinessScore = Math.max(0, Math.min(100, Math.round(
                ((c.body?.length >= 120 ? 100 : Math.min(c.body?.length || 0, 120) / 1.2) * 0.35) +
                ((c.hashtags ? 100 : 40) * 0.15) +
                ((c.status === "approved" || c.status === "published" ? 100 : c.status === "review" ? 65 : 40) * 0.25) +
                ((hasSchedule ? 100 : 35) * 0.25)
              )));
              const slaAlerts = [
                ...(c.status === "review" && reviewDays >= 2 ? [{ severity: "high", message: `In review for ${reviewDays} day(s)` }] : []),
                ...(c.status === "draft" && ageDays >= 7 ? [{ severity: "critical", message: `Draft is stale (${ageDays} days old)` }] : []),
                ...(c.status === "approved" && !hasSchedule ? [{ severity: "medium", message: "Approved but not scheduled for publishing" }] : []),
              ];
              return (
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{c.title}</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        By {c.createdBy?.name || "Unknown"} • {new Date(c.createdAt).toLocaleDateString()} •
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CFG[c.status]?.bg} ${STATUS_CFG[c.status]?.color}`}>{STATUS_CFG[c.status]?.label || c.status}</span>
                      </p>
                    </div>
                    <button onClick={() => setShowDetail(null)} className="p-1 text-gray-400 hover:text-gray-600"><XCircle className="w-5 h-5" /></button>
                  </div>

                  {/* Content Body */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <pre className="text-sm whitespace-pre-wrap text-gray-700 font-sans">{c.body}</pre>
                  </div>

                  {/* Meta row */}
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">{TYPE_ICONS[c.type] || "📄"} {c.type}</span>
                    {c.hashtags && <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> {c.hashtags}</span>}
                    {c.scheduledAt && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Scheduled: {new Date(c.scheduledAt).toLocaleString()}</span>}
                    {c.publishedAt && <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Published: {new Date(c.publishedAt).toLocaleString()}</span>}
                    {c.aiGenerated && <span className="flex items-center gap-1"><Sparkles className="w-3 h-3 text-purple-500" /> AI Generated</span>}
                  </div>

                  {/* Enterprise Governance */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
                      <p className="text-xs text-gray-500">Readiness Score</p>
                      <p className="text-2xl font-bold text-gray-900">{readinessScore}</p>
                      <div className="w-full h-2 bg-white rounded-full mt-2 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" style={{ width: `${readinessScore}%` }} />
                      </div>
                    </div>
                    <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-1">SLA Alerts</p>
                      <div className="space-y-1">
                        {slaAlerts.length > 0 ? slaAlerts.map((a: any, idx: number) => (
                          <p key={idx} className={`text-xs ${a.severity === "critical" ? "text-red-700" : a.severity === "high" ? "text-amber-700" : "text-blue-700"}`}>
                            {a.message}
                          </p>
                        )) : <p className="text-xs text-green-700">No active SLA risk.</p>}
                      </div>
                    </div>
                  </div>

                  {/* Workflow Actions */}
                  <div className="flex gap-2 border-t border-gray-100 pt-4">
                    {c.status === "draft" && <button onClick={() => submitM.mutate(c.id)} className="btn-secondary text-sm"><ArrowUpCircle className="w-4 h-4" /> Submit for Review</button>}
                    {c.status === "review" && (
                      <>
                        <button onClick={() => approveM.mutate(c.id)} className="btn-primary text-sm bg-green-600 hover:bg-green-700"><CheckCircle2 className="w-4 h-4" /> Approve</button>
                        <button onClick={() => rejectM.mutate({ id: c.id, comment: prompt("Reason for rejection:") || "Needs revision" })} className="btn-secondary text-sm text-red-600 border-red-200 hover:bg-red-50"><XCircle className="w-4 h-4" /> Reject</button>
                      </>
                    )}
                    {c.status === "approved" && <button onClick={() => publishM.mutate(c.id)} className="btn-primary text-sm"><Send className="w-4 h-4" /> Publish</button>}
                    <button onClick={() => duplicateM.mutate(c.id)} className="btn-secondary text-sm"><Copy className="w-4 h-4" /> Duplicate</button>
                  </div>

                  {/* Scheduled Posts */}
                  {c.scheduledPosts?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"><Calendar className="w-4 h-4" /> Scheduled Posts ({c.scheduledPosts.length})</h4>
                      <div className="space-y-2">
                        {c.scheduledPosts.map((sp: any) => (
                          <div key={sp.id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2.5">
                            <span className="text-sm capitalize font-medium text-gray-700">{sp.platformAccount?.platform}</span>
                            <span className="text-sm text-gray-500">{sp.platformAccount?.accountName}</span>
                            <span className="text-xs text-gray-400 ml-auto">{new Date(sp.scheduledAt).toLocaleString()}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${sp.status === "posted" ? "bg-green-50 text-green-600" : sp.status === "failed" ? "bg-red-50 text-red-600" : "bg-yellow-50 text-yellow-600"}`}>{sp.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Version History */}
                  {c.versions?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"><History className="w-4 h-4" /> Version History ({c.versions.length})</h4>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {c.versions.map((v: any) => (
                          <div key={v.id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2.5">
                            <span className="text-xs font-mono bg-white border border-gray-200 rounded px-1.5 py-0.5">v{v.version}</span>
                            <span className="text-sm text-gray-600 flex-1 truncate">{v.changeSummary || "No description"}</span>
                            <span className="text-xs text-gray-400">{new Date(v.createdAt).toLocaleDateString()}</span>
                            <button onClick={() => restoreM.mutate({ id: c.id, vId: v.id })} className="text-xs text-purple-600 hover:text-purple-800 font-medium"><RotateCcw className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Approval Log */}
                  {c.approvals?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Approval Log</h4>
                      <div className="space-y-2">
                        {c.approvals.map((a: any) => (
                          <div key={a.id} className="flex items-center gap-3 text-sm">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${a.action === "approved" ? "bg-green-50 text-green-600" : a.action === "rejected" ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"}`}>{a.action}</span>
                            {a.comment && <span className="text-gray-500 italic">"{a.comment}"</span>}
                            <span className="text-xs text-gray-400 ml-auto">{new Date(a.createdAt).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
